import {
  Card,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import type { FC } from 'react';
import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetTaxCodesByCountry,
  useGetTaxCodesByCountryPhoenix,
  useServiceProvider,
  useUpdateServiceProvider,
} from '@pflegenavi/service-provider-frontend/api';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { ServiceProviderReceiptBatchTaxSelectionView } from '../receipt-batch/form/ServiceProviderReceiptBatchTaxSelection';
import type { TaxItem } from '../receipt-batch/form/ServiceProviderReceiptBatchForm';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ServiceProviderSettingsProps {}

export const ServiceProviderSettings: FC<ServiceProviderSettingsProps> = memo(
  () => {
    const { t } = useTranslation();

    // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to exctract the globalServiceProviderId
    const { getTokenImmediate } = useAuthentication();

    const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
      getTokenImmediate()
    );
    const { data: serviceProvider } = useServiceProvider({
      globalServiceProviderId,
    });
    const { mutateAsync: updateServiceProvider } = useUpdateServiceProvider();

    const toggleAutoGenerateReceipts = useCallback(async () => {
      if (!globalServiceProviderId || !serviceProvider) {
        return;
      }
      await updateServiceProvider({
        globalServiceProviderId,
        data: {
          ...serviceProvider,
          defaultAutoGenerateReceipts:
            !serviceProvider.defaultAutoGenerateReceipts,
        },
      });
    }, [globalServiceProviderId, serviceProvider, updateServiceProvider]);

    const handleTaxItemChange = useCallback(
      async (taxItem: (TaxItem & { id: string | undefined }) | undefined) => {
        if (!globalServiceProviderId || !serviceProvider) {
          return;
        }
        await updateServiceProvider({
          globalServiceProviderId,
          data: {
            ...serviceProvider,
            defaultTaxCodeId: taxItem?.id,
          },
        });
      },
      [globalServiceProviderId, serviceProvider, updateServiceProvider]
    );

    if (!serviceProvider || !serviceProvider.countryId) {
      return null;
    }

    return (
      <Card sx={{ p: 3, maxWidth: 400 }}>
        <Stack direction="column" gap={2}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
            }}
          >
            {t('service-provider-app.service-provider-settings.title')}
          </Typography>

          <Stack gap={1}>
            <Stack>
              <Typography variant="caption">
                {t(
                  'service-provider-app.service-provider-settings.auto-generate-receipts-label'
                )}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    name={'autoGenerateReceipts'}
                    checked={serviceProvider.defaultAutoGenerateReceipts}
                    onChange={toggleAutoGenerateReceipts}
                  />
                }
                label={
                  serviceProvider.defaultAutoGenerateReceipts
                    ? t(
                        'service-provider-app.service-provider-settings.auto-generate-receipts-enabled'
                      )
                    : t(
                        'service-provider-app.service-provider-settings.auto-generate-receipts-disabled'
                      )
                }
              />
            </Stack>
            <Stack>
              <Typography variant="caption">
                {t(
                  'service-provider-app.service-provider-settings.default-tax-rate'
                )}
              </Typography>
              <TaxSelection
                countryId={serviceProvider.countryId}
                taxCodeId={serviceProvider.defaultTaxCodeId}
                handleTaxItemChange={handleTaxItemChange}
              />
            </Stack>
          </Stack>
        </Stack>
      </Card>
    );
  }
);

interface TaxSelectionProps {
  countryId: string;
  taxCodeId: string | undefined;
  handleTaxItemChange: (
    taxItem: (TaxItem & { id: string | undefined }) | undefined
  ) => void;
}
const TaxSelection: FC<TaxSelectionProps> = ({
  countryId,
  taxCodeId,
  handleTaxItemChange,
}) => {
  const { t } = useTranslation();
  const usePhoenix = useFeatureFlagEnabled(FeatureFlag.dev_ReceiptBatchPhoenix);
  const { data: taxConfigurations } = (
    usePhoenix ? useGetTaxCodesByCountryPhoenix : useGetTaxCodesByCountry
  )(countryId);

  const taxItems = useMemo<Array<TaxItem & { id: string | undefined }>>(() => {
    const taxes =
      taxConfigurations?.map<TaxItem & { id: string | undefined }>(
        (taxConfiguration) => ({
          id: taxConfiguration.id,
          taxName: taxConfiguration.name,
          countryId: taxConfiguration.countryId,
          displayExplanation: taxConfiguration.displayTaxRatioExplanation,
          taxAmount: taxConfiguration.taxRatio / 100,
          taxAmountInPercentage: taxConfiguration.taxRatio,
          explanationLong: taxConfiguration.taxRatioExplanation,
          paragraph: taxConfiguration.paragraph,
          // Todo: Currently there is no short explanation in the tax configuration
          explanationShort: undefined,
        })
      ) ?? [];

    return taxes.concat({
      id: undefined,
      taxName: t('service-provider-app.service-provider-settings.no-tax'),
      taxAmountInPercentage: 0,
      countryId: '',
      displayExplanation: true,
      taxAmount: 0,
      explanationLong: undefined,
      explanationShort: undefined,
      hidePercentage: true,
    });
  }, [t, taxConfigurations]);

  const selected = taxItems.find((item) => item.id === taxCodeId);

  return (
    <ServiceProviderReceiptBatchTaxSelectionView<
      TaxItem & { id: string | undefined }
    >
      taxItems={taxItems}
      selectedTaxItem={selected}
      handleTaxItemChange={handleTaxItemChange}
      handleClose={() => {
        return;
      }}
      error={undefined}
      touched={false}
    />
  );
};
